/**
 * LA LANGUE DU LECTEUR (§13) — et la seule mécanique de traduction du projet.
 *
 * ELLE TIENT EN UNE FONCTION : `t("Frapper")`. Le français reste ÉCRIT DANS LE CODE, à l'endroit où
 * il s'affiche, et c'est délibéré — `CLAUDE.md` demande le français partout dans le code, et une
 * table de clefs (`t("combat.frapper")`) rendrait chaque écran illisible sans un second fichier
 * ouvert à côté. Le français est donc la CLEF autant que la phrase : lire le composant, c'est lire
 * ce que le joueur voit.
 *
 * CE QUE ÇA COÛTE, ET COMMENT ON LE PAIE. Une clef-phrase se casse quand on retouche la
 * formulation : la traduction anglaise se détache en silence et l'écran repasse au français sans
 * que personne s'en aperçoive. Deux tests l'empêchent (`tests/traduction.test.ts`) : l'un balaie
 * les sources et exige qu'aucun `t("…")` ne manque au dictionnaire anglais, l'autre refuse une
 * entrée anglaise que plus personne n'emploie. Une traduction cassée devient donc un test rouge, ce
 * qui est le seul filet qui vaille.
 *
 * LA LANGUE VIT HORS DE REACT, et les écrans s'y abonnent (`useSyncExternalStore`). Un contexte
 * aurait obligé à faire passer la langue par tous les composants intermédiaires — y compris ceux
 * qui n'affichent rien — et le projet n'a pas de gestionnaire d'état pour une bonne raison : une
 * partie vit en base, pas dans un store. Une préférence de lecture, elle, vit dans le navigateur.
 */
import { useSyncExternalStore } from "react";

import { EN } from "./en.js";

export const LANGUES = ["fr", "en"] as const;
export type Langue = (typeof LANGUES)[number];

/** Le nom de chaque langue DANS SA PROPRE LANGUE : c'est la seule façon qu'un lecteur reconnaisse
 * la sienne dans une liste qu'il ne sait pas encore lire. */
export const NOMS_DE_LANGUE: Record<Langue, string> = { fr: "Français", en: "English" };

const CLEF = "destinia.langue";

/**
 * CE QUE LE LECTEUR A CHOISI, sinon ce que son navigateur annonce (§13.2). Pas de détection par
 * adresse IP : un Français en voyage reste un lecteur français, et deviner la langue d'après
 * l'endroit où l'on se trouve est une façon polie de se tromper.
 */
function detecte(): Langue {
  try {
    const garde = localStorage.getItem(CLEF);
    if (garde === "fr" || garde === "en") return garde;
  } catch {
    // Un navigateur qui refuse le stockage local n'est pas une panne : on retombe sur sa langue.
  }
  const dite = typeof navigator === "undefined" ? "fr" : (navigator.language ?? "fr");
  return dite.toLowerCase().startsWith("fr") ? "fr" : "en";
}

let courante: Langue = detecte();
const abonnes = new Set<() => void>();

export function langue(): Langue {
  return courante;
}

/**
 * LE TITRE DE L'ONGLET SUIT LA LANGUE (§13.2). Il est écrit en français dans `index.html` — c'est
 * ce que lisent les moteurs de recherche, qui n'exécutent pas notre javascript pour trouver un
 * titre — et réécrit ici dès que la page est montée. Un onglet français au-dessus d'une interface
 * anglaise est la dernière chose qui reste à traduire quand tout le reste l'est.
 *
 * La phrase est celle du pied de page : une seule à écrire, une seule à traduire.
 */
export function poseLeTitre(): void {
  if (typeof document === "undefined") return;
  document.title = t("Destinia — le livre dont on ne sort qu'une fois");
}

/** Changer de langue s'applique SUR-LE-CHAMP : on ne recharge pas une page de roman (§13.2). */
export function poseLaLangue(choisie: Langue): void {
  if (choisie === courante) return;
  courante = choisie;
  try {
    localStorage.setItem(CLEF, choisie);
  } catch {
    // Rien à faire : la langue tiendra le temps de la session, ce qui vaut mieux que de refuser.
  }
  if (typeof document !== "undefined") {
    document.documentElement.lang = choisie;
    poseLeTitre();
  }
  for (const abonne of abonnes) abonne();
}

function abonne(quoi: () => void): () => void {
  abonnes.add(quoi);
  return () => abonnes.delete(quoi);
}

/** L'abonnement des écrans. Un seul suffit — celui de la reliure —, mais rien n'interdit d'en
 * poser d'autres : un composant qui ne lit rien de traduit n'a pas besoin de se redessiner. */
export function useLangue(): Langue {
  return useSyncExternalStore(abonne, langue, () => "fr" as Langue);
}

/**
 * TRADUIRE. Le premier argument est la phrase française — elle sert de clef ET de repli : une
 * traduction manquante affiche du français plutôt qu'une clef nue, parce qu'un écran à moitié
 * traduit reste lisible et qu'un écran couvert de `combat.frapper` ne l'est pas.
 *
 * Les paramètres se posent en `{nom}` : `t("{n} case(s) portées", { n: 3 })`. On ne met JAMAIS un
 * nombre dans la clef — « 3 cases » deviendrait une entrée de dictionnaire par nombre possible.
 */
export function t(fr: string, params?: Record<string, string | number>): string {
  const phrase = courante === "fr" ? fr : (EN[fr] ?? fr);
  if (!params) return phrase;
  return phrase.replace(/\{(\w+)\}/g, (entier, clef: string) =>
    params[clef] === undefined ? entier : String(params[clef])
  );
}

/** Le pluriel, explicitement et sans moteur : deux phrases, on choisit. Les règles de pluriel d'une
 * langue ne se devinent pas, et une bibliothèque pour deux langues serait une dépendance de plus
 * pour ce que trois caractères font très bien.
 *
 * LE ZÉRO N'EST PAS PLURIEL PARTOUT, et c'est la seule subtilité : le français écrit « 0 tour »,
 * l'anglais « 0 turns ». La forme se choisit donc selon la langue AFFICHÉE, pas selon celle dans
 * laquelle la phrase a été écrite ; les deux clefs, elles, ne changent pas. */
export function tn(n: number, singulier: string, pluriel: string, params?: Record<string, string | number>): string {
  const auPluriel = courante === "fr" ? Math.abs(n) >= 2 : n !== 1;
  return t(auPluriel ? pluriel : singulier, { n, ...params });
}

/** LES CHAMPS QUI NE SONT PAS DU TEXTE dans les tables du lexique : une teinte est une variable
 * CSS, un glyphe est le nom d'un tracé. Les traduire n'aurait aucun sens, et les compter comme des
 * phrases à traduire ferait échouer le test de couverture pour `var(--sang)`. */
export const CHAMPS_SANS_LANGUE = ["teinte", "glyphe", "couleur", "empreinte", "clef"];

/**
 * UNE TABLE DU LEXIQUE, TRADUITE À LA LECTURE.
 *
 * Les tables du jeu — les états, les blessures, les raretés — sont lues à quatre-vingts endroits.
 * Les passer à `t()` à chaque endroit aurait fait quatre-vingts occasions d'en oublier une ; les
 * rendre fonctions aurait changé les quatre-vingts appels. On les remplace donc par une copie dont
 * chaque champ de texte est un ACCESSEUR : `ETATS.poison.nom` traduit au moment où on le lit, et
 * pas une ligne d'appelant ne bouge.
 *
 * Ce n'est pas de la magie — un accesseur est du JavaScript ordinaire, et il est défini ici, en un
 * seul endroit. Ce qui serait magique, ce serait un module qui se réécrirait tout seul au
 * changement de langue : ici, rien ne se réécrit, la lecture est simplement tardive.
 */
export function traduit<T>(source: T): T {
  /* LES TABLEAUX RESTENT INTACTS, ET C'EST VOULU. Un accesseur par indice serait une curiosité qu'on
     relirait trois fois ; et surtout, traduire les éléments ICI les figerait dans la langue du
     chargement — un tableau se construit d'un coup, il n'a pas d'accesseurs à la lecture. Les trois
     tableaux du lexique se passent donc à `t()` là où on les lit. */
  if (Array.isArray(source)) return source;
  if (source === null || typeof source !== "object") return source;

  const sortie = {} as Record<string, unknown>;
  for (const [clef, valeur] of Object.entries(source as Record<string, unknown>)) {
    if (typeof valeur === "string" && !CHAMPS_SANS_LANGUE.includes(clef)) {
      Object.defineProperty(sortie, clef, { get: () => t(valeur), enumerable: true, configurable: true });
    } else if (typeof valeur === "object" && valeur !== null) {
      sortie[clef] = traduit(valeur);
    } else {
      sortie[clef] = valeur;
    }
  }
  return sortie as T;
}
